import { add, multiplyWithFactor, subtract, } from "@pentacode/openapi";
import { getHolidaysInRange, isHoliday } from "../holidays";
import { AbsenceMode, NominalHoursMode } from "../model";
import { dateAdd, dateSub, getRange, parseDateString } from "../utils";
/**
 * Creates a getter to retrieve absence hours for a given date.
 *
 * If there is no exact match, it returns the closest preceeding value,
 * or undefined if there is no match within the last 13 weeks.
 *
 * This is relevant for calculating nominal hours when the `absenceMode` is set to `Average`
 * and the `nominalHoursMode` is set to `ExactWithoutHolidaysUsingAbsenceHours`.
 */
export const createAbsenceHoursGetter = (results) => {
    const sortedResults = results.sort((a, b) => a.date.localeCompare(b.date));
    return (date) => {
        let closest = undefined;
        for (const result of sortedResults) {
            if (result.date <= date) {
                if (!closest || result.date > closest.date) {
                    closest = result;
                }
            }
        }
        if (closest && closest.date < dateAdd(date, { weeks: -13 })) {
            console.warn(`No average hours found for date ${date}. Closest result is from ${closest.date}, which is more than 13 weeks ago.`);
            return undefined;
        }
        return closest?.averages["13weeks"].base.duration;
    };
};
export function getNominalTime(settings, company, emp, { from, to }, getAverageDailyWorkHours) {
    const startLedgers = settings.startLedgers;
    const contracts = emp.contracts.filter((c) => c.start <= to && (!c.end || c.end > from));
    const defaultPosition = emp.positions[0];
    const venue = (defaultPosition &&
        company.venues.find((v) => v.departments.some((d) => d.positions.some((p) => p.id === defaultPosition.id)))) ||
        company.venues[0];
    const holidays = venue?.enabledHolidays || undefined;
    const weekFactor = settings.weekFactor || 4.35;
    let total = 0;
    for (const contract of contracts) {
        const mode = contract.nominalHoursMode;
        let start = from >= contract.start ? from : contract.start;
        if (startLedgers && startLedgers > start) {
            start = startLedgers;
        }
        const end = !contract.end || contract.end > to ? to : contract.end;
        if (start > end) {
            start = end;
        }
        switch (mode) {
            case NominalHoursMode.WeekFactor:
                while (start < end) {
                    const { from, to } = getRange(start, "month");
                    const subEnd = to < end ? to : end;
                    const fraction = (dateSub(start, subEnd) / dateSub(from, to));
                    total = add(total, multiplyWithFactor(multiplyWithFactor(contract.hoursPerWeek, weekFactor), fraction));
                    start = subEnd;
                }
                break;
            case NominalHoursMode.Exact: {
                total = add(total, getNominalHoursInRange(contract.hoursPerWeek, start, end).nominalHoursTotal);
                break;
            }
            case NominalHoursMode.ExactWithoutHolidays: {
                const { nominalHoursTotal, nominalHoursPerDay } = getNominalHoursInRange(contract.hoursPerWeek, start, end);
                const holidaysInRange = getHolidaysInRange(start, end, { holidays, country: company.country }).length;
                const deductionHours = holidaysInRange * nominalHoursPerDay;
                total = add(total, nominalHoursTotal - deductionHours);
                break;
            }
            case NominalHoursMode.ExactWithoutHolidaysUsingAbsenceHours: {
                const { nominalHoursTotal, nominalHoursPerDay } = getNominalHoursInRange(contract.hoursPerWeek, start, end);
                const holidaysInRange = getHolidaysInRange(start, end, { holidays, country: company.country }).length;
                const deductionHoursPerDay = getDeductionHoursPerDay(contract, getAverageDailyWorkHours, start) || nominalHoursPerDay;
                total = add(total, (nominalHoursTotal - holidaysInRange * deductionHoursPerDay));
                break;
            }
            case NominalHoursMode.FixedDays:
            case NominalHoursMode.FixedDaysWithoutHolidays: {
                let day = start;
                while (day < end) {
                    const date = parseDateString(day);
                    const hours = contract.hoursPerDay?.[(date.getDay() + 6) % 7] || 0;
                    if (mode === NominalHoursMode.FixedDays ||
                        !isHoliday(date.getFullYear(), date.getMonth(), date.getDate(), {
                            holidays,
                            country: company.country,
                        })) {
                        total = add(total, hours);
                    }
                    day = dateAdd(day, { days: 1 });
                }
                break;
            }
        }
    }
    return total;
}
function getDeductionHoursPerDay(contract, getAverageDailyWorkHours, referenceDate) {
    if (contract.absenceMode === AbsenceMode.Average) {
        return getAverageDailyWorkHours?.(referenceDate);
    }
    else if (contract.absenceMode === AbsenceMode.Fixed) {
        return contract.absenceHours;
    }
}
function getNominalHoursInRange(weeklyHours, start, end) {
    const nominalHoursPerDay = (weeklyHours / 7);
    const daysInRange = dateSub(start, end);
    const nominalHoursTotal = (nominalHoursPerDay * daysInRange);
    return { nominalHoursTotal, nominalHoursPerDay };
}
/** Sums up the applicable sfnAdvances for the give DateRange basen on the contracts in that range */
export function getNominalBonus(allContracts, { from, to }) {
    // TODO: There should be a util fn "contractsInRange"
    const contracts = allContracts.filter((c) => c.start <= to && (!c.end || c.end > from));
    let total = 0;
    for (const contract of contracts) {
        let start = from >= contract.start ? from : contract.start; // This is a max function
        const end = !contract.end || contract.end > to ? to : contract.end; // This is a min function
        if (start > end) {
            start = end; // set to zero-length range if start is after end, this effectively skips all the following code.
        }
        while (start < end) {
            // [startDate, ...firstOfMonths, endDate]
            const { from, to } = getRange(start, "month");
            const subEnd = to < end ? to : end; // this is a min function
            // determine what fraction of the month we are looking at
            const fraction = (dateSub(start, subEnd) / dateSub(from, to));
            // add the fraction of the sfnAdvance of this month this contract/range pays out
            total = add(total, multiplyWithFactor(contract.sfnAdvance, fraction));
            start = subEnd;
        }
    }
    return total;
}
export function getVacationEntitlement(balance) {
    if (!balance) {
        return {
            available: 0,
            taken: 0,
            remaining: 0,
        };
    }
    const lastBalance = balance.subBalances?.length ? balance.subBalances[balance.subBalances.length - 1] : balance;
    const available = add(lastBalance.reset ?? lastBalance.carry, lastBalance.nominal);
    const taken = lastBalance.actual;
    return {
        available,
        taken,
        remaining: subtract(available, taken),
    };
}
//# sourceMappingURL=nominals.js.map